export const PL_MONTHS = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
] as const;

/** Human label like „Lipiec 2026" from 1-based month + year. */
export function periodLabel(year: number, month: number): string {
  return `${PL_MONTHS[month - 1]} ${year}`;
}

/** Year-month number (e.g. 202607) from an ISO "YYYY-MM-DD" date, or null. */
function ymFromIso(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m] = iso.split("-");
  const yn = Number(y), mn = Number(m);
  if (!yn || !mn) return null;
  return yn * 100 + mn;
}

/** Whether a 1-based (year, month) falls within a cost's [start_date, end_date] window. */
export function isPeriodInRange(
  year: number,
  month: number,
  startDate: string | null,
  endDate: string | null,
): boolean {
  const ym = year * 100 + month;
  const startYm = ymFromIso(startDate);
  if (startYm !== null && ym < startYm) return false;
  const endYm = ymFromIso(endDate);
  // Treat the far-future sentinel (2099+) as open-ended.
  if (endYm !== null && endYm < 209900 && ym > endYm) return false;
  return true;
}

/** Human-readable date range like „Styczeń 2026 – Grudzień 2026" or „od Styczeń 2026". */
export function formatRange(startDate: string | null, endDate: string | null): string {
  const start = ymFromIso(startDate);
  const startLabel = start ? periodLabel(Math.floor(start / 100), start % 100) : "—";
  const end = ymFromIso(endDate);
  if (end === null || end >= 209900) return `od ${startLabel}`;
  return `${startLabel} – ${periodLabel(Math.floor(end / 100), end % 100)}`;
}

interface MonthlyPeriodSelectProps {
  year: number;
  /** 1-based month (1 = January). */
  month: number;
  onChange: (year: number, month: number) => void;
}

/** Month + year selector for a monthly-invoice file's accounting period. */
export function MonthlyPeriodSelect({ year, month, onChange }: MonthlyPeriodSelectProps) {
  const thisYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = thisYear - 2; y <= thisYear + 1; y++) years.push(y);
  const cls = "border border-gray-300 rounded px-2 py-1.5 text-sm";
  return (
    <div className="flex gap-2">
      <select className={`${cls} flex-1`} value={month} onChange={(e) => onChange(year, Number(e.target.value))}>
        {PL_MONTHS.map((m, i) => (
          <option key={i + 1} value={i + 1}>{m}</option>
        ))}
      </select>
      <select className={`${cls} w-24`} value={year} onChange={(e) => onChange(Number(e.target.value), month)}>
        {years.map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}
